<script lang="ts" setup>
import { vOnClickOutside } from '@vueuse/components';
import type { SelectModel } from '@/shared/types';
import type { SelectProps as Props } from '@/shared/interfaces';
import useSelect from '@/shared/composables/use-select';
import IconAngle from '@/assets/icons/icon-angle.svg';

defineOptions({ inheritAttrs: false });

const props = withDefaults(defineProps<Props<SelectModel>>(), {
	description: '',
	placeholder: '',
	isError: false,
	errorMessage: '',
	required: false,
	disabled: false,
	isEdditable: false,
	color: 'white',
});

const emit = defineEmits<{
	(e: 'update:modelValue', val: SelectModel): void;
}>();

const {
	field,
	isOpen,
	isInput,
	computedClass,
	computedValue,
	computedTitle,
	computedEdditable,
	computedInput,
	computedListeners,
	optionClass,
	handleClose,
	handleToggle,
	handleOption,
	handleInputMode,
} = useSelect(props, emit);
</script>

<template>
	<div
		v-on-click-outside="handleClose"
		:class="['field', computedClass]"
	>
		<div v-if="label || !!$slots.default" class="field__header">
			<div v-if="label" class="field__header-item">
				<span class="field__label" v-text="label" />
			</div>

			<div v-if="!!$slots.default" class="field__header-item">
				<span class="field__additional">
					<slot />
				</span>
			</div>
		</div>

		<div class="field__wrapper">
			<div class="field__control">
				<input
					v-if="computedInput"
					ref="field"
					v-bind="$attrs"
					:value="computedValue"
					:readonly="!isInput"
					:required="required"
					:disabled="disabled"
					:placeholder="placeholder"
					:title="computedTitle"
					type="text"
					class="field__input"
					v-on="computedListeners"
				>
				<button
					v-else
					v-bind="$attrs"
					:title="computedTitle"
					:disabled="disabled"
					type="button"
					class="field__input button"
					v-on="computedListeners"
				>
					<span v-if="computedTitle !== ''" v-text="computedValue" />
					<span v-else class="field__input-placeholder" v-text="placeholder" />
				</button>

				<div class="field__action" @click="handleToggle">
					<icon-angle class="field__action-icon icon" />
				</div>
			</div>

			<transition name="fade">
				<v-scroll
					v-show="isOpen"
					tag="ul"
					class="field__select"
					appear
				>
					<li v-for="option in options" :key="option.id">
						<button
							class="field__select-item button"
							:class="optionClass(option)"
							@click="handleOption(option)"
						>
							<p v-text="option.value" />
						</button>
					</li>
					<li v-if="computedEdditable">
						<div class="field__select-item" @click="handleInputMode">
							another
						</div>
					</li>
				</v-scroll>
			</transition>
		</div>

		<div
			v-if="description.length > 0"
			class="field__description"
			v-text="description"
		/>

		<div v-if="isError" class="field__error">
			<template v-if="typeof errorMessage === 'object'">
				<div
					v-for="(message, index) in errorMessage"
					:key="index"
					class="mt-4"
					v-text="message"
				/>
			</template>
			<div
				v-else
				v-text="errorMessage"
			/>
		</div>
	</div>
</template>

<style lang="scss" scoped>
$gap-field: rem(10px) rem($gap-big) rem(10px) rem($gap-medium);
$gap-option: rem(10px) rem($gap-medium);
$icon-size: 21px;

$select-disabled-color: $dusty-gray;

$select-v-height: $select-height;

$select-background: $wild-sand;
$select-color: $black;
$select-border-color: $dusty-gray;
$select-icon-color: $dusty-gray;
$select-icon-hover-color: $dusty-gray;

$input-focus-color: $black;
$input-focus-icon-color: $input-focus-color;

$dropdown-background: $select-background;

$option-height: 36px;
$option-color: $select-color;
$option-selected-color: $select-color;
$option-selected-background: $select-background;
$option-disabled-color: darken($option-color, 35%);
$option-disabled-background: darken($option-selected-background, 35%);

$label-color: $black;
$label-disabled-color: $black;

$description-color: $black;
$error-color: $red;

.fade-enter-active,
.fade-leave-active {
	transition: opacity $transition-duration;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

.field {
	position: relative;

	&--open & {
		&__input {
			border-bottom-right-radius: 0;
			border-bottom-left-radius: 0;
			border-color: $input-focus-color;
		}

		&__action {
			background-color: $select-background;
		}

		&__select {
			border-color: $input-focus-color;
		}
	}

	&--error & {
		&__input {
			border-color: $error-color;

			&:focus,
			&:focus-visible {
				border-color: $error-color;
				outline: none;
			}

			&::placeholder {
				color: $error-color;
			}
		}

		&__input-placeholder {
			color: $error-color;
		}

		&__action {
			color: $error-color;

			&-icon {
				color: $error-color;

				@media (hover: hover) {
					&:hover {
						color: $error-color;
					}
				}
			}
		}
	}

	&--error &__input:focus + &__action {
		color: $error-color;
		border-color: $error-color;

		&__icon {
			@media (hover: hover) {
				&:hover {
					color: $error-color;
				}
			}
		}
	}

	&--required & {
		&__label {
			&::after {
				font-size: inherit;

				content: " *";

				color: $error-color;
			}
		}
	}

	&--disabled & {
		&__label {
			color: $label-disabled-color;
		}

		&__wrapper {
			pointer-events: none;
		}

		&__input,
		&__action {
			border-color: $select-disabled-color;
		}

		&__action {
			&-icon {
				color: $select-disabled-color;
			}
		}

		&__input {
			color: $select-disabled-color;
		}
	}

	&--white & {
		&__input {
			background-color: $white;
		}

		&__action {
			background-color: $white;
		}

		&__select {
			background-color: $white;
		}
	}

	&--gray & {
		&__input {
			background-color: $wild-sand;
		}

		&__action {
			background-color: $wild-sand;
		}

		&__select {
			background-color: $wild-sand;
		}
	}

	&__header {
		display: flex;

		margin-bottom: rem($gap-mini);

		&-item {
			flex: 1;

			&:nth-child(2) {
				text-align: right;
			}
		}
	}

	&__label {
		@include text-medium;

		color: $label-color;
	}

	&__additional {
		@include text-medium;
	}

	&__wrapper {
		position: relative;
	}

	&__control {
		position: relative;

		overflow: hidden;

		height: rem($select-v-height);
	}

	&__input {
		@include text-small;

		display: flex;
		overflow: hidden;
		align-items: center;

		width: 100%;
		height: rem($select-v-height);
		padding: $gap-field;

		cursor: pointer;
		transition:
			border-color $transition-duration $transition-function,
			background-color $transition-duration $transition-function;
		text-align: left;
		white-space: nowrap;
		text-overflow: clip;

		color: $select-color;
		border: rem(1px) solid $select-border-color;
		border-radius: rem($radius-secondary);

		&:focus,
		&:focus-visible {
			border-color: $input-focus-color;
			outline: none;
		}

		&::placeholder,
		&-placeholder {
			user-select: none;

			color: $text-darkblue;
		}

		&-placeholder {
			@include text-medium;
		}
	}

	&__input:focus + &__action {
		border-color: $dusty-gray;
	}

	&__action {
		position: absolute;
		top: rem(2px);
		right: rem(2px);
		bottom: rem(2px);

		display: flex;
		align-items: center;
		justify-content: center;

		width: rem(48px);

		cursor: pointer;
		user-select: none;
		transition:
			border-color $transition-duration $transition-function,
			background-color $transition-duration $transition-function;

		border: none;
		border-radius: rem($radius-secondary);

		&-icon {
			width: rem($icon-size);
			height: rem($icon-size);

			transition: color $transition-function $transition-duration;
			transform: translateX(rem(-1px)) rotate(90deg);

			color: $select-icon-color;
		}
	}

	&__select {
		position: absolute;
		z-index: $z-index-dropdown;
		top: 100%;
		left: 0;

		overflow: hidden;

		width: 100%;
		max-height: rem(240px);

		list-style: none;

		transition:
			border-right $transition-duration $transition-function,
			border-bottom $transition-duration $transition-function,
			border-left $transition-duration $transition-function;

		border-right: rem(1px) solid $select-border-color;
		border-bottom: rem(1px) solid $select-border-color;
		border-left: rem(1px) solid $select-border-color;
		border-bottom-right-radius: rem($radius-primary);
		border-bottom-left-radius: rem($radius-primary);

		&-item {
			@include text-small;

			display: flex;
			overflow: hidden;
			align-items: center;

			width: 100%;
			min-height: rem($option-height);
			padding: $gap-option;

			text-align: left;

			cursor: pointer;
			transition: background-color $transition-duration $transition-function;

			color: $option-color;

			@media (hover: hover) {
				&:hover {
					color: $option-selected-color;
					background-color: $option-selected-background;
				}
			}

			&--active {
				font-weight: bold;
				background-color: $option-selected-background;
			}

			&--active {
				cursor: pointer;
			}

			&--disabled {
				cursor: default;
				user-select: none;

				color: $option-disabled-color;
				background-color: $option-disabled-background;

				@media (hover: hover) {
					&:hover {
						background-color: $option-disabled-background;
					}
				}
			}
		}
	}

	&__description {
		@include text-mini;

		margin-top: rem($gap-mini);

		color: $description-color;
	}

	&__error {
		@include text-mini;

		margin-top: rem($gap-mini);

		color: $error-color;
	}
}
</style>
